import * as React from 'react';
import { AccentTemplateTextAreaUnbound } from '../unbound/AccentTemplateTextAreaUnbound';
import { AiWandButton } from '../ai/AiWandButton';
import { showDialog, t } from '../../services/HelperService';
import { AccentWorkflowJavascriptAssistantDlg } from './AccentWorkflowJavascriptAssistantDlg';
import { mapFixedPicklists, toJavascriptStringLiteral } from './templates/fixedPicklistLiteralCatalog';

export const AccentWorkflowJavascriptEditor = ({
  label,
  required,
  readonly,
  defaultValue,
  onChange,
  templateCatalog,
  javascriptTemplateCatalog,
  entityFieldCatalog,
  rows = 6,
  max = 8000,
  style,
  dataTagName,
  workflowDefinitionID,
  scriptKind = 'expression',
  variableName,
  runtimeNotes,
  aiStorageKey,
  initialVariablesJson,
  initialTemplateValues,
  // The assistant's *_ChatStart query (async chat transport); see the dialog.
  chatStartQuery,
  tryFixQuery,
  explainQuery,
  describeQuery,
  executeTest,
  testButtonLabel,
  requestContext,
  contextRoot = 'ctx',
  /**
   * Hides the overlay AI wand. Hosts whose script can only be tested meaningfully inside a
   * combined query + script session (e.g. Wait For (Query), where ctx.query is bound by the
   * executed query) set this and expose that session from a section-header wand instead.
   */
  hideAi,
}) => {
  const [editorKey, setEditorKey] = React.useState(0);
  const [scriptText, setScriptText] = React.useState(() => safeString(defaultValue));

  React.useEffect(() => {
    setScriptText(safeString(defaultValue));
  }, [defaultValue]);

  const doOnChange = React.useCallback((value) => {
    const nextScript = safeString(value);
    setScriptText(nextScript);
    onChange?.(nextScript);
  }, [onChange]);

  const javascriptCatalog = React.useMemo(() => {
    const base = Array.isArray(javascriptTemplateCatalog)
      ? javascriptTemplateCatalog
      : (Array.isArray(templateCatalog) ? templateCatalog : []);
    return normalizeJavascriptCatalog(mapFixedPicklists(base, toJavascriptStringLiteral), contextRoot);
  }, [contextRoot, javascriptTemplateCatalog, templateCatalog]);
  const promptTemplateCatalog = React.useMemo(() => (
    entityFieldCatalog ? [...javascriptCatalog, entityFieldCatalog] : javascriptCatalog
  ), [entityFieldCatalog, javascriptCatalog]);

  const sharedRuntimeNotes = React.useMemo(() => ([
    t('JavaScript snippets use ctx.*. For example: ctx.vars.myValue, ctx.settings.MySetting, ctx.workflowEntity.ID.'),
    t('ctx.vars contains hydrated plain JavaScript values. Do not write scripts against the persisted { type, value } debug/storage shape.'),
    t('Query result variables keep the API result shape. If a query selects ID, Product, DiscountedPriceIncTax, rows use row.ID, row.Product, row.DiscountedPriceIncTax.'),
    t('Only use nested properties when the query result actually includes nested or expanded objects.'),
  ]), []);
  const combinedRuntimeNotes = React.useMemo(() => ([
    ...sharedRuntimeNotes,
    ...(Array.isArray(runtimeNotes) ? runtimeNotes : []),
  ]), [runtimeNotes, sharedRuntimeNotes]);
  const hydratedVariablesPreviewJson = React.useMemo(
    () => formatHydratedVariablesPreview(initialVariablesJson),
    [initialVariablesJson]
  );
  const aiRequestContext = React.useMemo(() => ({
    ...(requestContext ?? {}),
    hydratedVariablesPreviewJson,
  }), [hydratedVariablesPreviewJson, requestContext]);

  const onAskAI = React.useCallback(async (e) => {
    try {
      const result = await showDialog(
        <AccentWorkflowJavascriptAssistantDlg
          existingScript={scriptText}
          scriptKind={scriptKind}
          variableName={variableName}
          workflowDefinitionID={workflowDefinitionID}
          scriptTemplateCatalog={javascriptCatalog}
          promptTemplateCatalog={promptTemplateCatalog}
          runtimeNotes={combinedRuntimeNotes}
          storageKey={aiStorageKey}
          label={label}
          initialVariablesJson={initialVariablesJson}
          initialTemplateValues={initialTemplateValues}
          chatStartQuery={chatStartQuery}
          tryFixQuery={tryFixQuery}
          explainQuery={explainQuery}
          describeQuery={describeQuery}
          executeTest={executeTest}
          testButtonLabel={testButtonLabel}
          requestContext={aiRequestContext}
        />
      );

      if (result?.canceled) return;
      const nextScript = safeString(result?.script);
      setScriptText(nextScript);
      onChange?.(nextScript);
      setEditorKey(k => k + 1);
    } finally {
      e?.promise?.();
    }
  }, [aiRequestContext, aiStorageKey, chatStartQuery, combinedRuntimeNotes, describeQuery, executeTest, explainQuery, initialTemplateValues, initialVariablesJson, javascriptCatalog, label, onChange, promptTemplateCatalog, scriptKind, scriptText, testButtonLabel, tryFixQuery, variableName, workflowDefinitionID]);

  return (
    <AccentTemplateTextAreaUnbound
      key={editorKey}
      label={label ?? t('JavaScript expression')}
      required={!!required}
      disabled={!!readonly}
      rows={rows}
      max={max}
      defaultValue={scriptText}
      onChange={doOnChange}
      templateCatalog={javascriptCatalog}
      insertMode="snippet"
      style={{ width: '100%', ...(style ?? {}) }}
      dataTagName={dataTagName}
      overlayButtons={(readonly || hideAi) ? undefined : [
        <AiWandButton
          key="ai"
          tagName="aiWorkflowJavascriptAssistant"
          overlay
          title={t('AI Assistant')}
          onClick={onAskAI}
        />,
      ]}
    />
  );
};

const safeString = (v) => (v ?? '').toString();

const parseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const hydrateTypedVariable = (node) => {
  if (!node || typeof node !== 'object') return node ?? null;
  const type = safeString(node.type ?? node.Type).toLowerCase();
  const value = Object.prototype.hasOwnProperty.call(node, 'value') ? node.value : node.Value;
  if (!type) return node;
  if (type === 'null') return null;
  if (type === 'string') return value ?? null;
  if (type === 'boolean') return !!value;
  if (type === 'number') return typeof value === 'number' ? value : Number(value);
  if (type === 'date') return value ?? null;
  if (type === 'array') return Array.isArray(value) ? value.map(hydrateTypedVariable) : [];
  if (type === 'object') {
    const obj = {};
    Object.entries(value ?? {}).forEach(([key, child]) => {
      obj[key] = hydrateTypedVariable(child);
    });
    return obj;
  }
  return value ?? null;
};

const nestFlatVariables = (source) => {
  const result = {};
  Object.entries(source ?? {}).forEach(([key, value]) => {
    const parts = key.split('.');
    let current = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]] || typeof current[parts[i]] !== 'object') current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
  });
  return result;
};

const formatHydratedVariablesPreview = (variablesJson) => {
  const parsed = parseJson(variablesJson, {});
  const vars = parsed?.vars ?? parsed?.Vars ?? {};
  const flat = {};
  Object.entries(vars ?? {}).forEach(([key, node]) => {
    flat[key] = hydrateTypedVariable(node);
  });
  try {
    return JSON.stringify(nestFlatVariables(flat), null, 2);
  } catch {
    return '{}';
  }
};

const workflowRuntimeRoots = [
  'vars',
  'settings',
  'trigger',
  'triggerEntity',
  'workflowEntity',
  'workflow',
  'tenant',
  'run',
  'wait',
  'auditEntry',
  'err',
];

export const normalizeJavascriptCatalog = (items, contextRoot) => {
  const root = safeString(contextRoot).trim();
  const normalizeItem = (item) => {
    if (!item || typeof item !== 'object') return item;
    if (Array.isArray(item.items)) {
      return {
        ...item,
        items: item.items.map(normalizeItem),
      };
    }
    return {
      ...item,
      token: normalizeJavascriptSnippet(item.token, root),
      snippet: normalizeJavascriptSnippet(item.snippet || item.token, root),
      label: item.label,
    };
  };

  return (Array.isArray(items) ? items : []).map(normalizeItem);
};

const normalizeJavascriptSnippet = (value, contextRoot) => {
  const raw = safeString(value).trim();
  if (!raw) return raw;

  const unwrapped = raw.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
  if (!unwrapped) return raw;
  if (!contextRoot) return unwrapped;
  if (unwrapped === contextRoot || unwrapped.startsWith(`${contextRoot}.`)) return unwrapped;
  if (/^[A-Za-z_$][\w$]*\s*\(/.test(unwrapped)) return raw;

  const root = workflowRuntimeRoots.find(r => unwrapped === r || unwrapped.startsWith(`${r}.`));
  return root ? `${contextRoot}.${unwrapped}` : raw;
};
